import type {
  LocationQuery,
  LocationQueryValue,
  RouteLocationNormalized,
  RouteLocationRaw,
} from 'vue-router'
import type Corporate from '@shared/data/models/corporate.js'
import ROUTE from '@eu/app/router/appModule/names.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import * as apiRequest from '@shared/api-endpoints'
import { decodeQuery } from '@shared/utils.js'
import useSharedStore from '@shared/stores/useSharedStore.js'
import useDoctorOutreachStore from '@eu/app/stores/useDoctorOutreachStore.js'

// Global EU session bootstrap. Runs before route `beforeEnter` guards.
// Keep only cross-route or query-reactive navigation rules here.

type QueryParamValue = LocationQueryValue | LocationQueryValue[] | undefined
type RedirectTarget = RouteLocationRaw | undefined

const parseNonEmptyQueryValue = (value: QueryParamValue): string | undefined =>
  typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined

export const consumeFromQuery = (
  query: LocationQuery,
  key: string,
): QueryParamValue => {
  const value = query[key]
  delete query[key]
  return value
}

/** Merges decoded query values into the stored initial query. */
export const storeInitialNavigationQuery = (query: LocationQuery): void => {
  useSharedStore().storeInitialQuery(decodeQuery(query))
}

/** userStore.user survives invalidateTokens() — double-check tokens are
 *  still present to catch the race where a fire-and-forget 401 wiped them
 *  while router.push() was already in flight. */
export const redirectWhenTokensInvalidated = (): RedirectTarget =>
  !useAuthStore().isAuthenticated ? { name: ROUTE.SESSION_EXPIRED } : undefined

export const resolveRenewalPractice = async (): Promise<void> => {
  const initialQuery = useSharedStore().initialQuery as {
    subscription_renewal?: string
    doctor_practice_id?: string
  }
  if (initialQuery.subscription_renewal !== 'true') return
  if (!/^\d+$/.test(initialQuery.doctor_practice_id ?? '')) return

  await useDoctorOutreachStore().resolvePractice(
    Number(initialQuery.doctor_practice_id),
  )
}

/** Resolves the requested corporate, falling back to the profile corporate. */
export const resolveSessionCorporate = async (
  requestedCorporate: QueryParamValue,
): Promise<Corporate | null> => {
  const corporateStore = useCorporateStore()
  const corporateKey = parseNonEmptyQueryValue(requestedCorporate)

  if (corporateKey) {
    const corporate = await corporateStore.resolveCorporate(corporateKey)
    if (corporate) return corporate
  }

  // The session branch guarantees a loaded user.
  return corporateStore.resolveCorporate(
    useUserStore().user!.profile.corporateId,
  )
}

export const syncProfileCorporate = async (
  corporate: Corporate,
): Promise<void> => {
  if (useUserStore().user!.profile.corporateId !== corporate.id) {
    await apiRequest.setUserProfile({ corporateId: corporate.id })
  }
}

// Must remain global: the token can arrive in a same-route query update,
// which does not rerun `beforeEnter`.
export const redirectToHukVerification = (
  corporate: Corporate,
  to: RouteLocationNormalized,
): RedirectTarget =>
  corporate.key === 'huk' &&
  to.query.token != null &&
  to.name !== ROUTE.INSURANCE_VERIFICATION_HUK
    ? { name: ROUTE.INSURANCE_VERIFICATION_HUK, query: to.query }
    : undefined

// Must remain global: `corporate` can arrive in a same-route query update.
export const redirectResolvedCorporateToSignup = async (
  to: RouteLocationNormalized,
  from?: RouteLocationNormalized,
): Promise<RedirectTarget> => {
  if (to.name !== ROUTE.INSURANCE_SELECTION) return
  if (from?.name === ROUTE.INSURANCE_COVERED) return

  const requestedCorporate = parseNonEmptyQueryValue(to.query.corporate)
  if (!requestedCorporate) return

  const corporate =
    await useCorporateStore().resolveCorporate(requestedCorporate)
  if (corporate) return { name: ROUTE.SIGN_UP, query: to.query }
}

export const requireAuthenticated = (
  to: RouteLocationNormalized,
): RedirectTarget =>
  to.meta?.public ? undefined : { name: ROUTE.SIGN_UP, query: to.query }

export default async (
  to: RouteLocationNormalized,
  from?: RouteLocationNormalized,
): Promise<RedirectTarget> => {
  storeInitialNavigationQuery(to.query)

  if (useUserStore().user === null) {
    const signupTarget = await redirectResolvedCorporateToSignup(to, from)
    if (signupTarget) return signupTarget

    return requireAuthenticated(to)
  }

  if (to.name === ROUTE.SESSION_EXPIRED) return

  const sessionExpiredTarget = redirectWhenTokensInvalidated()
  if (sessionExpiredTarget) return sessionExpiredTarget

  await resolveRenewalPractice()

  // Visitors keep `corporate` for sign-up; authenticated sessions consume it.
  const corporate = await resolveSessionCorporate(
    consumeFromQuery(to.query, 'corporate'),
  )
  if (corporate == null) return

  await syncProfileCorporate(corporate)

  return redirectToHukVerification(corporate, to)
}
